// Card "Em destaque" na home, ao lado dos outros 3.
// Lista compacta de provas em andamento + próximas avaliações que pedem
// ação. Some quando não há nada.

import { ChevronRight, Play, Rocket } from "lucide-react";
import type {
  ProvaWithBank,
  StudentHomeResponse,
  UpcomingAssessment
} from "./api";

interface Props {
  home: StudentHomeResponse;
  onOpenItem: (a: UpcomingAssessment) => void;
  onOpenProvaAtiva: (p: ProvaWithBank) => void;
}

export function DestaqueHomeCard({
  home,
  onOpenItem,
  onOpenProvaAtiva
}: Props) {
  const upcomingActionable = home.upcoming.filter(
    (a) => a.status === "running" || a.status === "scheduled"
  );
  const provasAtivas = home.provasAtivas;
  const totalCount = provasAtivas.length + upcomingActionable.length;
  const isEmpty = totalCount === 0;

  return (
    <section class="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
      <header class="mb-3 flex items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 text-sm font-bold text-amber-700 dark:text-amber-400">
          <Rocket className="size-4" />
          Em destaque
          {totalCount > 0 && (
            <span class="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-amber-500/15 px-1.5 text-[11px] font-bold tabular-nums text-amber-700 ring-1 ring-amber-500/25 dark:text-amber-400">
              {totalCount}
            </span>
          )}
        </h2>
      </header>
      {isEmpty && (
        <p class="rounded-md border border-dashed border-amber-500/20 bg-background/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          Nada em andamento agora. Comece um estudo ou aguarde uma prova.
        </p>
      )}
      {!isEmpty && (
      <ul class="space-y-1.5">
        {provasAtivas.slice(0, 3).map((p) => (
          <li
            key={`p-${p.id ?? p.bankId}`}
            onClick={() => onOpenProvaAtiva(p)}
            class="flex cursor-pointer items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 hover:bg-amber-500/15"
          >
            <span class="mt-0.5 inline-block size-2 shrink-0 animate-pulse rounded-full bg-amber-500" />
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="truncate text-xs font-semibold">{p.bankName}</span>
                <span class="rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  em andamento
                </span>
              </div>
              <div class="mt-0.5 truncate text-[11px] text-muted-foreground">
                {p.respondidas}/{p.total} questões — clique pra continuar
              </div>
            </div>
            <Play className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          </li>
        ))}
        {upcomingActionable.slice(0, 3 - Math.min(provasAtivas.length, 3)).map((a) => (
          <li
            key={`u-${a.id}`}
            class="flex items-start gap-2 rounded-md border border-border bg-background/60 px-3 py-2 hover:bg-accent/50 cursor-pointer"
            onClick={() => onOpenItem(a)}
          >
            <span class="mt-0.5 inline-block size-2 shrink-0 rounded-full bg-amber-500" />
            <div class="min-w-0 flex-1">
              <div class="truncate text-xs font-semibold">{a.title}</div>
              <div class="mt-0.5 truncate text-[11px] text-muted-foreground">
                {a.status === "running" ? "Em andamento" : "Agendada"}
              </div>
            </div>
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" />
          </li>
        ))}
      </ul>
      )}
    </section>
  );
}
